/**
 * Shared snapping utilities for shift-key constrained interactions.
 * Used for rotation snapping (15° increments), line endpoint snapping (45° increments),
 * and position axis constraint (horizontal/vertical only).
 */

/**
 * Rotation snap increments (every 15 degrees).
 * Used when holding Shift during element rotation.
 */
export const ROTATION_SNAP_INCREMENTS = [
  0,
  15,
  30,
  45,
  60,
  75,
  90,
  105,
  120,
  135,
  150,
  165,
  180,
  195,
  210,
  225,
  240,
  255,
  270,
  285,
  300,
  315,
  330,
  345,
  360
];

/**
 * Line angle snap increments (every 45 degrees).
 * Used when holding Shift during line endpoint drag.
 */
export const LINE_ANGLE_INCREMENTS = [0, 45, 90, 135, 180, 225, 270, 315, 360];

/**
 * Snap an angle to the nearest increment in the given list.
 * Handles 360/0 wrap-around correctly.
 *
 * @param angle - Angle in degrees (any range)
 * @param increments - Array of angle increments to snap to
 * @returns Snapped angle (360 is normalized to 0)
 */
export function snapAngleToIncrement(
  angle: number,
  increments: number[] = ROTATION_SNAP_INCREMENTS
): number {
  let nearestAngle: number = increments[0] ?? 0;
  let minDiff = Infinity;

  for (const inc of increments) {
    let diff = Math.abs(angle - inc);
    // Handle wrap-around at 360 degrees
    if (diff > 180) {
      diff = 360 - diff;
    }
    if (diff < minDiff) {
      minDiff = diff;
      nearestAngle = inc;
    }
  }

  // Normalize 360 to 0
  return nearestAngle === 360 ? 0 : nearestAngle;
}

/**
 * Constrain a point to the nearest angle increment from a pivot point.
 * Preserves the distance from pivot while snapping the direction.
 *
 * @param pivotX - X coordinate of the fixed pivot point
 * @param pivotY - Y coordinate of the fixed pivot point
 * @param pointX - X coordinate of the point to constrain
 * @param pointY - Y coordinate of the point to constrain
 * @param targetAngles - Angles to snap to (defaults to LINE_ANGLE_INCREMENTS)
 * @returns Constrained point coordinates
 */
export function constrainPointToAngle(
  pivotX: number,
  pivotY: number,
  pointX: number,
  pointY: number,
  targetAngles: number[] = LINE_ANGLE_INCREMENTS
): { x: number; y: number } {
  const dx = pointX - pivotX;
  const dy = pointY - pivotY;
  const length = Math.sqrt(dx * dx + dy * dy);

  let currentAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  if (currentAngle < 0) currentAngle += 360;

  const snappedAngle = snapAngleToIncrement(currentAngle, targetAngles);
  const angleRad = snappedAngle * (Math.PI / 180);

  return {
    x: pivotX + length * Math.cos(angleRad),
    y: pivotY + length * Math.sin(angleRad)
  };
}

/**
 * Constrain a movement delta to the horizontal or vertical axis.
 * The axis is determined by which component has larger magnitude ("nearest axis wins").
 * Equal magnitude defaults to horizontal.
 *
 * @param deltaX - Horizontal delta
 * @param deltaY - Vertical delta
 * @returns Constrained delta with minor axis set to zero
 */
export function constrainDeltaToAxis(
  deltaX: number,
  deltaY: number
): { deltaX: number; deltaY: number } {
  if (Math.abs(deltaX) >= Math.abs(deltaY)) {
    return { deltaX, deltaY: 0 };
  }
  return { deltaX: 0, deltaY };
}
